import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag } from 'lucide-react';

export default function Cart() {
    const { cartItems, cartTotal, updateQuantity, removeFromCart, loading } = useCart();
    const { user } = useAuth();
    const navigate = useNavigate();

    if (loading) return <div className="text-center py-12">Loading...</div>;

    if (cartItems.length === 0) {
        return (
            <div className="max-w-7xl mx-auto px-4 py-16 text-center">
                <ShoppingBag className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                <h2 className="text-2xl font-bold mb-4">Your cart is empty</h2>
                <p className="text-gray-600 mb-6">Add some delicious items from our menu!</p>
                <Link to="/menu" className="btn-primary">Browse Menu</Link>
            </div>
        );
    }

    // Calculate item count and check stock validation
    const itemCount = cartItems.reduce((sum, item) => sum + (parseInt(item.quantity) || 1), 0);
    const hasStockErrors = cartItems.some(item => parseInt(item.quantity) > parseInt(item.stock_quantity));

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-24 md:pb-8">
            <h1 className="text-3xl font-bold mb-8">Shopping Cart</h1>

            <div className="grid lg:grid-cols-3 gap-8">
                {/* Cart Items */}
                <div className="lg:col-span-2 space-y-4">
                    {cartItems.map((item) => {
                        const itemTotal = (parseFloat(item.price) || 0) * (parseInt(item.quantity) || 1);

                        return (
                            <div key={item.id} className="bg-white p-4 rounded-lg shadow flex flex-col sm:flex-row items-start sm:items-center gap-4">
                                <div className="w-24 h-24 bg-gray-200 rounded-lg overflow-hidden shrink-0">
                                    {item.image_url ? (
                                        <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">No Image</div>
                                    )}
                                </div>

                                <div className="flex-1">
                                    <h3 className="font-semibold text-lg">{item.name}</h3>
                                    <p className="text-gray-600 text-sm">₹{parseFloat(item.price).toFixed(2)} each</p>
                                    {item.special_instructions && (
                                        <p className="text-gray-500 text-xs mt-1">Note: {item.special_instructions}</p>
                                    )}
                                </div>

                                <div className="flex items-center space-x-2">
                                    <button
                                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                        className="p-1 rounded-full hover:bg-gray-100"
                                    >
                                        <Minus className="w-4 h-4" />
                                    </button>
                                    <span className="font-medium w-8 text-center">{item.quantity}</span>
                                    <button
                                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                        className="p-1 rounded-full hover:bg-gray-100 disabled:opacity-50"
                                        disabled={parseInt(item.quantity) >= parseInt(item.stock_quantity)}
                                        title={parseInt(item.quantity) >= parseInt(item.stock_quantity) ? "Maximum stock reached" : ""}
                                    >
                                        <Plus className="w-4 h-4" />
                                    </button>
                                </div>

                                <div className="text-right min-w-[100px]">
                                    <p className="font-bold">₹{itemTotal.toFixed(2)}</p>
                                    <p className="text-xs text-gray-500">({item.quantity} × ₹{parseFloat(item.price).toFixed(2)})</p>
                                    {parseInt(item.quantity) > parseInt(item.stock_quantity) && (
                                        <p className="text-xs text-red-600 mt-1 font-bold">Out of stock! Only {item.stock_quantity} available.</p>
                                    )}
                                    <button
                                        onClick={() => removeFromCart(item.id)}
                                        className="text-red-500 hover:text-red-700 text-sm flex items-center mt-1"
                                    >
                                        <Trash2 className="w-4 h-4 mr-1" />
                                        Remove
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Order Summary - Simplified without delivery fee */}
                <div className="bg-white p-6 rounded-lg shadow h-fit">
                    <h2 className="text-xl font-bold mb-4">Order Summary</h2>

                    {/* Item breakdown */}
                    <div className="space-y-2 mb-4 max-h-40 overflow-y-auto">
                        {cartItems.map(item => {
                            const itemTotal = (parseFloat(item.price) || 0) * (parseInt(item.quantity) || 1);
                            return (
                                <div key={item.id} className="flex justify-between text-sm">
                                    <span>{item.name} × {item.quantity}</span>
                                    <span>₹{itemTotal.toFixed(2)}</span>
                                </div>
                            );
                        })}
                    </div>

                    <div className="border-t pt-4 space-y-2">
                        <div className="flex justify-between text-lg font-bold">
                            <span>Subtotal ({itemCount} items)</span>
                            <span>₹{cartTotal.toFixed(2)}</span>
                        </div>

                        <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-700">
                            <p>💡 Delivery fee will be calculated at checkout based on your location</p>
                        </div>
                    </div>


                    {hasStockErrors && (
                        <div className="bg-red-50 p-3 rounded-lg text-sm text-red-700 mt-4 font-bold border border-red-200">
                            Some items are out of stock for the requested quantity. Please adjust them before proceeding.
                        </div>
                    )}

                    <button
                        onClick={() => {
                            if (!user) {
                                const activeBranchSlug = localStorage.getItem('active_branch_slug');
                                navigate(`/login?redirect=/cart&branch=${activeBranchSlug || ''}`);
                            } else {
                                navigate('/checkout');
                            }
                        }}
                        disabled={hasStockErrors}
                        className="w-full btn-primary py-3 text-lg mt-4 disabled:opacity-50 disabled:cursor-not-allowed hidden-disabled:bg-gray-400"
                    >
                        Proceed to Checkout
                    </button>

                    <Link to="/menu" className="block text-center mt-4 text-primary-600 hover:underline">
                        Continue Shopping
                    </Link>
                </div>
            </div>
        </div>
    );
}